import React from 'react'
import {Link} from 'react-router-dom'
import clsx from 'clsx'

export interface StripeBannerCta {
  label: string
  background?: string
  color?: string
}

export interface StripeBannerProps {
  /** Stripe background color (the variant's fill) */
  background: string
  /** Text color — set inline so light-background variants render dark text. Default white. */
  textColor?: string
  /** Appends `stripe-banner--{variant}` for variant-scoped responsive rules */
  variant?: string
  /** White close (X) for dark backgrounds (default); set false for the dark X on light backgrounds */
  closeWhite?: boolean
  /** Left-side icon/logo group */
  icon?: React.ReactNode
  /** Text content (use stripe-banner__title|subtext|link helper classes) */
  children: React.ReactNode
  /** Right-side CTA — the ONLY clickable jump target; degrades to an underlined link below 600px */
  cta?: StripeBannerCta
  /** Internal route the CTA links to */
  to?: string
  /** External href the CTA links to */
  href?: string
  external?: boolean
  /** Renders the close (X) button when provided */
  onClose?: () => void
  className?: string
}

// Material `keyboard_arrow_right` — inline so it inherits the CTA text color via currentColor.
const ChevronRight = () => (
  <svg
    className='stripe-banner__cta-arrow'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    aria-hidden='true'
  >
    <path d='M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z' fill='currentColor' />
  </svg>
)

const StripeBanner: React.FC<StripeBannerProps> = ({
  background,
  textColor = '#FFFFFF',
  variant,
  closeWhite = true,
  icon,
  children,
  cta,
  to,
  href,
  external,
  onClose,
  className,
}) => {
  const ctaStyle = cta ? {backgroundColor: cta.background, color: cta.color} : undefined
  const ctaInner = cta && (
    <>
      {cta.label}
      <ChevronRight />
    </>
  )

  // Only the CTA is clickable — the stripe itself is not a link.
  const renderCta = () => {
    if (!cta) return null
    if (to) {
      return (
        <Link to={to} className='stripe-banner__cta' style={ctaStyle}>
          {ctaInner}
        </Link>
      )
    }
    if (href) {
      return (
        <a
          href={href}
          target={external ? '_blank' : undefined}
          rel='noopener noreferrer'
          className='stripe-banner__cta'
          style={ctaStyle}
        >
          {ctaInner}
        </a>
      )
    }
    return (
      <span className='stripe-banner__cta' style={ctaStyle}>
        {ctaInner}
      </span>
    )
  }

  return (
    <div
      className={clsx('stripe-banner', variant && `stripe-banner--${variant}`, className)}
      style={{backgroundColor: background, color: textColor}}
    >
      <div className='stripe-banner__body'>
        {icon && <div className='stripe-banner__icon'>{icon}</div>}
        {children}
      </div>

      {renderCta()}

      {onClose && (
        <button
          type='button'
          className={clsx('stripe-banner__close btn-close', {'btn-close-white': closeWhite})}
          onClick={onClose}
          aria-label='Close'
        />
      )}
    </div>
  )
}

export {StripeBanner}
